import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserProfile } from "@/types";
import ProfileService from "@/services/profileService";
import { getGlobalSessionId } from "@/services";

export const userProfileKeys = {
  all: ["userProfile"] as const,
  profile: (sessionId: string) => [...userProfileKeys.all, sessionId] as const,
};

export const useUserProfile = () => {
  const sessionId = getGlobalSessionId();

  return useQuery({
    queryKey: userProfileKeys.profile(sessionId || ""),
    queryFn: () => {
      if (!sessionId) {
        throw new Error("No session ID available");
      }
      return ProfileService.getUserProfile();
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const sessionId = getGlobalSessionId();

  return useMutation({
    mutationFn: (profileData: Partial<UserProfile>) => {
      if (!sessionId) {
        throw new Error("No session ID available");
      }
      return ProfileService.updateUserProfile(profileData);
    },
    onSuccess: (updatedProfile) => {
      if (updatedProfile && sessionId) {
        // Update the cache with new profile data
        queryClient.setQueryData(
          userProfileKeys.profile(sessionId),
          updatedProfile
        );
      }
    },
    onError: (error) => {
      console.error("Failed to update profile:", error);
    },
  });
};
